// Self-hosted Qwen Image Edit API
//
// The server now runs a two-stage pipeline:
//   1. NSFW filter on every request (prompt + image). Unsafe inputs are rejected
//      with `error_code: "nsfw_blocked"` before any GPU work.
//   2. AI prompt enhancement / routing — for `mode: "erase"` the server can
//      either write its own prompt from the outlined region or skip enhancement
//      and use the backend default static prompt.
//
// `POST /predictions` returns a job id, poll
// `GET /predictions/{id}` until `status` is "succeeded" or "failed".

import type { AiVariantPromptStrategy } from "@/core/types"
import { fetchWithNetworkBehavior } from "./fetch-with-network-behavior"

export type QwenMode = "generate" | "erase"
export type QwenErrorCode = "nsfw_blocked" | "internal_error"

export interface QwenReferenceImage {
	data: string
	mimeType: string
}

export interface GenerateQwenImageEditRequest {
	mode?: QwenMode // default "generate"
	prompt?: string // required for "generate", ignored for "erase"
	base64Image: string // generate: main image. erase: clean cropped patch.
	base64OutlinedImage?: string // erase: same patch but with the user's red outline
	base64ReferenceImages?: QwenReferenceImage[] // generate-only, optional
	mimeType: string
	skipPromptEnhancement?: boolean // erase-only: bypass VLM prompt enhancer/router
}

export interface QwenVariation {
	data: string
	mimeType: string
	enhancedPrompt: string | null
	promptStrategy?: AiVariantPromptStrategy
}

export interface GenerateQwenImageEditResponse {
	variations: QwenVariation[]
	/** Prompt actually fed into the diffusion model. For generate this is the user prompt verbatim; for erase it is AI-written. */
	enhancedPrompt: string | null
	mode: QwenMode
}

/** Typed error so call sites can branch on the failure category without parsing strings. */
export class QwenApiError extends Error {
	public readonly errorCode: QwenErrorCode
	public readonly detail?: string

	constructor(errorCode: QwenErrorCode, message: string, detail?: string) {
		super(message)
		this.name = "QwenApiError"
		this.errorCode = errorCode
		this.detail = detail
	}
}

interface PredictionMetrics {
	mode?: QwenMode
	total_time_seconds?: number
	original_size?: string
	processing_size?: string
	output_format?: string
	num_images?: number
	nsfw_check?: {
		is_nsfw: boolean
		reason: string
		parsed_ok: boolean
		elapsed_seconds: number
	}
	prompt_enhancement?: {
		enhanced_prompt: string
		use_outlined_image: boolean
		raw_response: string | null
		elapsed_seconds: number
	}
}

interface PredictionResponse {
	id: string
	status: "pending" | "processing" | "succeeded" | "failed"
	created_at: string
	started_at: string | null
	completed_at: string | null
	input: Record<string, unknown> | null
	output: string | null
	error: string | null
	error_code: QwenErrorCode | null
	enhanced_prompt: string | null
	metrics: PredictionMetrics | null
}

/**
 * Base URL for the self-hosted Qwen API.
 *
 * Override at build time via `VITE_QWEN_API_BASE_URL`. Otherwise falls back
 * to the default below.
 */
export const DEFAULT_QWEN_API_BASE_URL = "https://qwen-image-edit-2511-inference.dev.polarr.co"

export const resolveQwenApiBaseUrl = (envValue: unknown): string => {
	if (typeof envValue !== "string") return DEFAULT_QWEN_API_BASE_URL

	const trimmed = envValue.trim()
	if (!trimmed) return DEFAULT_QWEN_API_BASE_URL

	return trimmed.replace(/\/+$/, "")
}

export const QWEN_API_BASE_URL: string = resolveQwenApiBaseUrl(
	import.meta.env.VITE_QWEN_API_BASE_URL,
)

const QWEN_VARIATION_COUNT = 2

// Warm requests are 10–20s. NSFW filter adds 1–3s, prompt routing in erase mode
// adds another ~2s. Keep the user-facing request timeout capped at 1 minute.
const POLL_INTERVAL_MS = 1500
const POLL_MAX_DURATION_MS = 60_000

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const base64ToBlob = (base64: string, mimeType: string): Blob => {
	const binaryString = atob(base64)
	const bytes = new Uint8Array(binaryString.length)
	for (let i = 0; i < binaryString.length; i++) {
		bytes[i] = binaryString.charCodeAt(i)
	}
	return new Blob([bytes], { type: mimeType })
}

const extensionForMime = (mimeType: string): "png" | "jpg" =>
	mimeType.includes("png") ? "png" : "jpg"

const stripDataUrlPrefix = (s: string): string => {
	if (!s.startsWith("data:")) return s
	const commaIndex = s.indexOf(",")
	return commaIndex === -1 ? s : s.slice(commaIndex + 1)
}

interface PreparedQwenRequestImages {
	mainBlob: Blob
	mainExt: "png" | "jpg"
	outlinedBlob?: Blob
	referenceImages: Array<{
		blob: Blob
		ext: "png" | "jpg"
	}>
}

const prepareQwenRequestImages = (
	req: GenerateQwenImageEditRequest,
	mode: QwenMode,
): PreparedQwenRequestImages => {
	const mainBlob = base64ToBlob(req.base64Image, req.mimeType)
	const mainExt = extensionForMime(req.mimeType)

	if (mode === "erase") {
		if (!req.base64OutlinedImage) {
			throw new QwenApiError("internal_error", "erase mode requires base64OutlinedImage")
		}

		return {
			mainBlob,
			mainExt,
			outlinedBlob: base64ToBlob(req.base64OutlinedImage, req.mimeType),
			referenceImages: [],
		}
	}

	return {
		mainBlob,
		mainExt,
		referenceImages:
			req.base64ReferenceImages?.map((ref) => ({
				blob: base64ToBlob(ref.data, ref.mimeType),
				ext: extensionForMime(ref.mimeType),
			})) ?? [],
	}
}

export const getQwenVariationPromptStrategy = (
	mode: QwenMode,
	options?: { skipPromptEnhancement?: boolean },
): AiVariantPromptStrategy => {
	if (mode !== "erase") {
		return "standard"
	}

	return options?.skipPromptEnhancement ? "default_prompt" : "prompt_enhanced"
}

/** Submit a prediction job. Throws QwenApiError on synchronous (HTTP 4xx/5xx) failure. */
const submitPrediction = async (
	req: GenerateQwenImageEditRequest,
	mode: QwenMode,
	images: PreparedQwenRequestImages,
	options?: { skipPromptEnhancement?: boolean },
): Promise<PredictionResponse> => {
	const formData = new FormData()
	formData.append("mode", mode)

	formData.append("images", images.mainBlob, `input.${images.mainExt}`)

	if (mode === "erase") {
		if (!images.outlinedBlob) {
			throw new QwenApiError("internal_error", "erase mode requires outlined image blob")
		}
		formData.append("outlined_image", images.outlinedBlob, `outlined.${images.mainExt}`)
		if (options?.skipPromptEnhancement) {
			formData.append("skip_prompt_enhancement", "true")
			formData.append("prompt", req.prompt ?? "")
		}
	} else {
		// generate mode: append optional reference images and the user prompt
		images.referenceImages.forEach((ref, index) => {
			formData.append("images", ref.blob, `ref${index + 1}.${ref.ext}`)
		})
		formData.append("prompt", req.prompt ?? "")
	}

	formData.append("negative_prompt", "")
	formData.append("seed", Math.floor(Math.random() * 1_000_000).toString())
	formData.append("output_format", "jpeg")
	formData.append("jpeg_quality", "99")

	const totalImages = mode === "erase" ? 2 : 1 + images.referenceImages.length
	const mainSizeKB = (images.mainBlob.size / 1024).toFixed(1)
	console.log(
		`📤 Qwen submit: mode=${mode}, skipPromptEnhancement=${Boolean(
			options?.skipPromptEnhancement,
		)}, ${totalImages} image(s), main=${mainSizeKB}KB (${req.mimeType}), prompt="${(req.prompt ?? "").slice(0, 50)}${
			(req.prompt ?? "").length > 50 ? "..." : ""
		}"`,
	)

	const response = await fetchWithNetworkBehavior(
		`${QWEN_API_BASE_URL}/predictions`,
		{ method: "POST", body: formData },
		{ networkIntent: "active" },
	)

	if (!response.ok) {
		const errorText = await response.text()
		console.error(`❌ Qwen submit error: ${response.status} ${errorText}`)
		throw new QwenApiError(
			"internal_error",
			`Qwen API error (${response.status}): ${errorText}`,
			errorText,
		)
	}

	return (await response.json()) as PredictionResponse
}

const pollUntilDone = async (jobId: string): Promise<PredictionResponse> => {
	const startTime = Date.now()
	let pollCount = 0

	while (Date.now() - startTime <= POLL_MAX_DURATION_MS) {
		await sleep(POLL_INTERVAL_MS)
		pollCount++

		const response = await fetchWithNetworkBehavior(
			`${QWEN_API_BASE_URL}/predictions/${jobId}`,
			{ method: "GET" },
			// Polling is background work: retry transient connectivity drops silently
			// and avoid repeatedly surfacing active-request offline toasts.
			{ networkIntent: "background" },
		)

		if (!response.ok) {
			const errorText = await response.text()
			throw new QwenApiError(
				"internal_error",
				`Failed to poll prediction (${response.status}): ${errorText}`,
				errorText,
			)
		}

		const result = (await response.json()) as PredictionResponse
		console.log(`🔄 Qwen status: ${result.status} (poll ${pollCount})`)

		if (result.status === "succeeded" || result.status === "failed") {
			return result
		}
	}

	throw new QwenApiError(
		"internal_error",
		`Prediction timed out after ${POLL_MAX_DURATION_MS / 1000}s`,
	)
}

const generateSingleVariation = async (
	req: GenerateQwenImageEditRequest,
	mode: QwenMode,
	images: PreparedQwenRequestImages,
	variationIndex: number,
	totalVariations: number,
	options?: { skipPromptEnhancement?: boolean },
): Promise<{ data: string; enhancedPrompt: string | null; mode: QwenMode }> => {
	console.log(
		`🔍 Qwen variation ${variationIndex + 1}/${totalVariations} (mode=${mode}, skipPromptEnhancement=${Boolean(
			options?.skipPromptEnhancement,
		)})`,
	)

	const initial = await submitPrediction(req, mode, images, options)
	console.log(`📝 Qwen job submitted: ${initial.id}`)

	const final = await pollUntilDone(initial.id)

	if (final.status === "failed") {
		const errorCode: QwenErrorCode = final.error_code ?? "internal_error"
		const message = final.error ?? "Qwen prediction failed"
		console.error(`❌ Qwen prediction failed (${errorCode}): ${message}`)
		throw new QwenApiError(errorCode, message, final.error ?? undefined)
	}

	if (!final.output) {
		throw new QwenApiError("internal_error", "Qwen prediction succeeded but returned no output")
	}

	console.log(
		`🎉 Qwen variation ${variationIndex + 1} done in ${
			final.metrics?.total_time_seconds ?? "?"
		}s`,
	)

	return {
		data: stripDataUrlPrefix(final.output),
		enhancedPrompt: final.enhanced_prompt,
		mode: final.metrics?.mode ?? mode,
	}
}

/**
 * Generate image edit using self-hosted Qwen API.
 *
 * Errors:
 *  - `QwenApiError` with `errorCode: "nsfw_blocked"` — input was rejected by
 *    the safety filter. Treat as a normal user-facing state (ask them to revise).
 *  - `QwenApiError` with `errorCode: "internal_error"` — generic failure.
 */
export const generateQwenImageEdit = async (
	req: GenerateQwenImageEditRequest,
): Promise<GenerateQwenImageEditResponse> => {
	const mode: QwenMode = req.mode ?? "generate"
	console.log(`🎨 Starting Qwen generation (mode=${mode})...`)

	const images = prepareQwenRequestImages(req, mode)
	const variationConfigs =
		mode === "erase"
			? [{ skipPromptEnhancement: false }, { skipPromptEnhancement: true }]
			: Array.from({ length: QWEN_VARIATION_COUNT }, () => ({
					skipPromptEnhancement: req.skipPromptEnhancement,
				}))

	const variationRequests = variationConfigs.map((config, index) =>
		generateSingleVariation(req, mode, images, index, variationConfigs.length, config),
	)

	const settled = await Promise.allSettled(variationRequests)

	const variations: QwenVariation[] = []
	let enhancedPrompt: string | null = null
	const errors: unknown[] = []

	settled.forEach((result, index) => {
		if (result.status === "fulfilled") {
			variations.push({
				data: result.value.data,
				mimeType: "image/jpeg",
				enhancedPrompt: result.value.enhancedPrompt,
				promptStrategy: getQwenVariationPromptStrategy(mode, variationConfigs[index]),
			})
			enhancedPrompt = enhancedPrompt ?? result.value.enhancedPrompt
		} else {
			console.error(`❌ Qwen variation ${index + 1} failed:`, result.reason)
			errors.push(result.reason)
		}
	})

	if (variations.length === 0) {
		// Prefer NSFW signal — input is the same across variations so they all share fate.
		const nsfwError = errors.find(
			(e): e is QwenApiError => e instanceof QwenApiError && e.errorCode === "nsfw_blocked",
		)
		if (nsfwError) throw nsfwError

		const firstError = errors[0]
		if (firstError instanceof QwenApiError) throw firstError
		throw new QwenApiError("internal_error", "Qwen image generation failed for all variations.")
	}

	console.log(`✅ Qwen success — ${variations.length} variation(s), mode=${mode}`)
	return { variations, enhancedPrompt, mode }
}
